import { AiCodingStatus } from "../aicoding/AiCodingApi";
import debug from "../debug";
import { Field } from "../schemas";
import { SchemaContext } from "../schemas/SchemaContext";

/**
 * Utility class that handles processing, transformation, and text manipulation
 * for AI-generated content. Manages text escaping, replacement tagging, and
 * conversion between schema paths and code references.
 */
export class AiCodingProcessor {
  static processAiCodingResults(result: AiCodingStatus, fields: Field[]) {
    debug.log((l) => l("AiCodingProcessor.processAiCodingResults", result, fields));

    return result.items
      .map((item) => ({
        originalText: item.originalText,
        surroundingText: this.stripExistingTags(item.surroundingText),
        code: this.convertPathToCode(item.pathToValue),
      }))
      .filter((item) => {
        const field = fields.find((field) => field.key === item.code.split(".")[0]);
        if (!field) {
          console.error(`Failed to find field for ${item.code}`);
          return false;
        }
        const node = SchemaContext.findSchema(item.code, field.store.schema);
        if (!node) {
          console.error(`Failed to find schema for ${item.code}`);
          return false;
        }
        return true;
      });
  }

  static convertPathToCode(path: string): string {
    return path
      .replace(/^#\/definitions\//, "")
      .replace(/properties\//g, "")
      .replace(/\//g, ".");
  }

  static escapeNewlines(text: string): string {
    return text.replace(/\r/g, "<line_break>").replace(/\v/g, "<vertical_tab>");
  }

  static unescapeNewlines(text: string): string {
    return text.replace(/<line_break>/g, "\r").replace(/<vertical_tab>/g, "\v");
  }

  static markAsReplaced(uniqueTexts: Set<string>, bodyText: string): string {
    return Array.from(uniqueTexts).reduce((text, controlText) => this.markTextAsReplaced(controlText, text), bodyText);
  }

  static markTextAsReplaced(text: string, bodyText: string): string {
    // Escape regex special characters
    const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return bodyText.replace(new RegExp(escapedText, "g"), `<already_replaced>${text}</already_replaced>`);
  }

  static stripExistingTags(text: string): string {
    const stripped = text.replace(/<already_replaced>/g, "").replace(/<\/already_replaced>/g, "");
    return this.unescapeNewlines(stripped);
  }
}
